import api from './api';
import { assertCurrentSession, captureSession } from '../utils/authSession';

const AUTHORIZE_BASE = 'https://appcenter.intuit.com/connect/oauth2';
const SCOPE = 'com.intuit.quickbooks.accounting com.intuit.quickbooks.payment';
const LINK_KEYS = ['client_id', 'response_type', 'scope', 'redirect_uri', 'state'];
// The backend issues ten-minute links. Allow one minute of clock skew, but never an unbounded timer.
const MAX_LINK_LIFETIME_MS = 11 * 60 * 1000;
const invalid = () => new Error('QuickBooks setup could not be verified.');
const object = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

function unwrap(response) {
  if (!object(response?.data) || response.data.success !== true || !object(response.data.data)) throw invalid();
  return response.data.data;
}

/** Keep provider identifiers and unused diagnostics out of component state. This is not a live probe. */
export function validateQuickBooksStatus(value) {
  if (!object(value) || typeof value.configured !== 'boolean' || typeof value.connectConfigured !== 'boolean' ||
      !['production', 'sandbox'].includes(value.environment)) throw invalid();
  return { configured: value.configured, connectConfigured: value.connectConfigured, environment: value.environment };
}

/** Validate again at click time: timers can be throttled while a browser tab is in the background. */
export function validateQuickBooksLink(value, now = Date.now()) {
  try {
    if (!object(value) || typeof value.authorizeUrl !== 'string' || value.authorizeUrl.length > 4096 ||
        !value.authorizeUrl.startsWith(`${AUTHORIZE_BASE}?`) || /[\s\\]/.test(value.authorizeUrl)) throw invalid();
    const url = new URL(value.authorizeUrl);
    if (url.protocol !== 'https:' || url.hostname !== 'appcenter.intuit.com' || url.port ||
        url.username || url.password || url.pathname !== '/connect/oauth2' || url.hash) throw invalid();
    const params = url.searchParams;
    if ([...params.keys()].length !== LINK_KEYS.length || LINK_KEYS.some((key) => params.getAll(key).length !== 1) ||
        !params.get('client_id') || /\s/.test(params.get('client_id')) || params.get('response_type') !== 'code' ||
        params.get('scope') !== SCOPE || !/^[a-f0-9]{64}$/.test(params.get('state'))) throw invalid();
    const redirect = new URL(params.get('redirect_uri'));
    if (redirect.protocol !== 'https:' || redirect.username || redirect.password || redirect.port || redirect.hash ||
        /[\s\\]/.test(params.get('redirect_uri'))) throw invalid();
    const expires = typeof value.expiresAt === 'string' ? Date.parse(value.expiresAt) : NaN;
    if (!Number.isFinite(now) || !Number.isFinite(expires) || new Date(expires).toISOString() !== value.expiresAt ||
        expires <= now || expires > now + MAX_LINK_LIFETIME_MS) throw invalid();
    return { authorizeUrl: url.href, expiresAt: value.expiresAt };
  } catch {
    throw invalid();
  }
}

const quickBooksService = {
  async getStatus(sessionBinding = captureSession()) {
    try {
      const response = await api.get('/admin/qb/status', { sessionBinding, headers: { 'Cache-Control': 'no-store' }, timeout: 20000 });
      assertCurrentSession(sessionBinding);
      return validateQuickBooksStatus(unwrap(response));
    } catch {
      throw invalid();
    }
  },
  async beginConnect(sessionBinding = captureSession()) {
    try {
      // This GET creates a single-use nonce. An uncertain response must not trigger automatic replay.
      const response = await api.get('/admin/qb/connect', {
        sessionBinding, skipAuthReplay: true, headers: { 'Cache-Control': 'no-store' }, timeout: 20000,
      });
      assertCurrentSession(sessionBinding);
      return validateQuickBooksLink(unwrap(response));
    } catch {
      throw invalid();
    }
  },
};

export default quickBooksService;
